import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Tag, Receipt, Store as StoreIcon, TrendingUp } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Confirmation {
  item_id:      string
  price_paid:   number
  confirmed_at: string
  was_on_sale:  boolean | null
  store_name?:  string | null
}

interface PantryItem {
  id:    string
  name:  string
  emoji: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function getMonthKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// ─── Spending ─────────────────────────────────────────────────────────────────

export default function Spending() {
  const { user } = useAuth()

  // ── Fetch household id ────────────────────────────────────────────────────

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .maybeSingle()
      return data?.household_id as string ?? null
    },
  })

  // ── Fetch confirmations ───────────────────────────────────────────────────

  const { data: confirmations = [], isLoading } = useQuery({
    queryKey: ['confirmations-spending', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, price_paid, confirmed_at, was_on_sale, store_name')
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: false })
      return (data ?? []) as Confirmation[]
    },
  })

  // ── Fetch pantry items for names ──────────────────────────────────────────

  const { data: pantryItems = [] } = useQuery({
    queryKey: ['pantry-items-names', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, emoji')
        .eq('household_id', householdId!)
      return (data ?? []) as PantryItem[]
    },
  })

  const itemMap = useMemo(() => {
    const m: Record<string, PantryItem> = {}
    for (const item of pantryItems) m[item.id] = item
    return m
  }, [pantryItems])

  // ── Monthly totals (last 6 months) ────────────────────────────────────────

  const monthlyData = useMemo(() => {
    const months: { key: string; label: string; total: number; saleTotal: number; count: number }[] = []
    const now = new Date()

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      months.push({
        key:       getMonthKey(d.toISOString()),
        label:     monthLabel(d),
        total:     0,
        saleTotal: 0,
        count:     0,
      })
    }

    for (const c of confirmations) {
      const key = getMonthKey(c.confirmed_at)
      const m   = months.find(m => m.key === key)
      if (!m) continue
      m.total += c.price_paid
      m.count += 1
      if (c.was_on_sale) m.saleTotal += c.price_paid
    }

    return months
  }, [confirmations])

  const maxMonthTotal = Math.max(...monthlyData.map(m => m.total), 1)

  // ── This month vs last month ──────────────────────────────────────────────

  const thisMonth = monthlyData[monthlyData.length - 1]
  const lastMonth = monthlyData[monthlyData.length - 2]
  const monthDiff = thisMonth.total - lastMonth.total
  const monthDiffPct = lastMonth.total > 0
    ? Math.round((monthDiff / lastMonth.total) * 100)
    : 0

  // ── Total saved on sales ──────────────────────────────────────────────────

  const totalSaved = useMemo(() => {
    return confirmations
      .filter(c => c.was_on_sale)
      .reduce((sum, c) => sum + c.price_paid, 0)
  }, [confirmations])

  const saleCount = confirmations.filter(c => c.was_on_sale).length

  // ── Top items by spend ────────────────────────────────────────────────────

  const topItems = useMemo(() => {
    const byItem: Record<string, { total: number; count: number; lastPrice: number }> = {}
    for (const c of confirmations) {
      if (!byItem[c.item_id]) byItem[c.item_id] = { total: 0, count: 0, lastPrice: 0 }
      byItem[c.item_id].total     += c.price_paid
      byItem[c.item_id].count     += 1
      byItem[c.item_id].lastPrice  = c.price_paid
    }
    return Object.entries(byItem)
      .map(([id, data]) => ({
        id,
        name:      itemMap[id]?.name  ?? 'Unknown',
        emoji:     itemMap[id]?.emoji ?? '📦',
        total:     data.total,
        count:     data.count,
        avgPrice:  data.total / data.count,
        lastPrice: data.lastPrice,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
  }, [confirmations, itemMap])

  // ── Price alerts — items where latest price > avg by 15%+ ────────────────

  const priceAlerts = useMemo(() => {
    const byItem: Record<string, number[]> = {}
    const sorted = [...confirmations].sort(
      (a, b) => new Date(a.confirmed_at).getTime() - new Date(b.confirmed_at).getTime()
    )
    for (const c of sorted) {
      if (!byItem[c.item_id]) byItem[c.item_id] = []
      byItem[c.item_id].push(c.price_paid)
    }

    const alerts: { id: string; name: string; emoji: string; avgPrice: number; latestPrice: number; pctChange: number }[] = []

    for (const [id, prices] of Object.entries(byItem)) {
      if (prices.length < 2) continue
      const latest  = prices[prices.length - 1]
      const history = prices.slice(0, -1)
      const avg     = history.reduce((s, p) => s + p, 0) / history.length
      const pct     = ((latest - avg) / avg) * 100
      if (pct >= 15) {
        alerts.push({
          id,
          name:        itemMap[id]?.name  ?? 'Unknown',
          emoji:       itemMap[id]?.emoji ?? '📦',
          avgPrice:    avg,
          latestPrice: latest,
          pctChange:   Math.round(pct),
        })
      }
    }

    return alerts.sort((a, b) => b.pctChange - a.pctChange).slice(0, 5)
  }, [confirmations, itemMap])

  // ── Top stores ────────────────────────────────────────────────────────────

  const topStores = useMemo(() => {
    const byStore: Record<string, { total: number; count: number }> = {}
    for (const c of confirmations) {
      if (!c.store_name) continue
      if (!byStore[c.store_name]) byStore[c.store_name] = { total: 0, count: 0 }
      byStore[c.store_name].total += c.price_paid
      byStore[c.store_name].count += 1
    }
    return Object.entries(byStore)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5)
  }, [confirmations])

  const totalAllTime = confirmations.reduce((s, c) => s + c.price_paid, 0)

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-[100dvh] bg-cream pb-28">

      {/* ── Masthead ── */}
      <header className="px-4 pt-6 pb-5">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
          Insights
        </div>
        <h1 className="font-serif text-[28px] font-normal leading-[1.05] tracking-[-0.015em] text-ink">
          Your <em className="italic">spending</em>
        </h1>
        <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.4]">
          What you've logged across shops.
        </p>
      </header>

      {isLoading ? (
        <div className="px-4 flex flex-col gap-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="h-32 rounded-[18px] border border-border-sand bg-paper animate-pulse"
            />
          ))}
        </div>
      ) : confirmations.length === 0 ? (
        <div className="mx-4 mt-8 rounded-[18px] border border-border-sand bg-paper p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-[14px] bg-cream-warm">
            <Receipt className="h-[22px] w-[22px] text-terracotta" strokeWidth={1.6} />
          </div>
          <h2 className="font-serif text-[18px] font-medium text-ink mb-1">
            Nothing logged <em className="italic">yet</em>.
          </h2>
          <p className="font-serif text-[13px] italic text-ink-muted leading-[1.5]">
            Log prices when you shop and your spending will appear here.
          </p>
        </div>
      ) : (
        <div className="px-4 flex flex-col gap-4">

          {/* ── This month ── */}
          <section className="rounded-[18px] border border-border-sand bg-paper p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                This month
              </div>
              {lastMonth.total > 0 && (
                <div
                  className={`rounded-full px-3 py-1 text-[11px] font-medium ${
                    monthDiff <= 0
                      ? 'bg-ok-bg text-forest'
                      : 'bg-warn-bg text-terracotta'
                  }`}
                >
                  {monthDiff <= 0
                    ? `${Math.abs(monthDiffPct)}% less than last month`
                    : `${Math.abs(monthDiffPct)}% more than last month`}
                </div>
              )}
            </div>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-serif text-[34px] font-medium text-ink leading-none">
                ${thisMonth.total.toFixed(2)}
              </span>
            </div>
            <p className="font-serif text-[12px] italic text-ink-muted">
              {thisMonth.count} {thisMonth.count === 1 ? 'purchase' : 'purchases'} logged
            </p>

            {totalSaved > 0 && (
              <div className="mt-4 rounded-[12px] bg-ok-bg border border-forest/10 p-3 flex items-center gap-3">
                <Tag className="h-[16px] w-[16px] text-forest flex-shrink-0" strokeWidth={1.7} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[14px] font-medium text-forest">
                    ${totalSaved.toFixed(2)} saved on sales
                  </div>
                  <div className="font-serif text-[11px] italic text-forest/70">
                    across {saleCount} sale {saleCount === 1 ? 'purchase' : 'purchases'}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── 6-month chart ── */}
          <section className="rounded-[18px] border border-border-sand bg-paper p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-4">
              Last 6 months
            </div>

            <div className="flex items-end gap-2 h-32">
              {monthlyData.map((m, i) => {
                const heightPct = maxMonthTotal > 0 ? (m.total / maxMonthTotal) * 100 : 0
                const isCurrentMonth = i === monthlyData.length - 1
                return (
                  <div key={m.key} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[10px] text-ink-muted font-medium font-serif">
                      {m.total > 0 ? `$${Math.round(m.total)}` : ''}
                    </div>
                    <div className="w-full flex flex-col justify-end" style={{ height: 80 }}>
                      <div
                        className={`w-full rounded-t-[4px] transition-all ${
                          isCurrentMonth ? 'bg-forest' : 'bg-ok-bg'
                        }`}
                        style={{
                          height: `${Math.max(heightPct, m.total > 0 ? 4 : 0)}%`,
                          minHeight: m.total > 0 ? 4 : 0,
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-ink-muted tracking-[0.04em] font-serif italic">
                      {m.label.split(' ')[0]}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-4 pt-3 border-t border-border-hairline flex justify-between items-baseline">
              <span className="font-serif text-[12px] italic text-ink-muted">All-time total</span>
              <span className="font-serif text-[14px] font-medium text-ink">
                ${totalAllTime.toFixed(2)}
              </span>
            </div>
          </section>

          {/* ── Price alerts ── */}
          {priceAlerts.length > 0 && (
            <section className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-[14px] w-[14px] text-terracotta" strokeWidth={1.8} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Price increases
                </div>
              </div>
              <div className="rounded-[14px] border border-border-sand overflow-hidden">
                {priceAlerts.map((alert, idx) => (
                  <div
                    key={alert.id}
                    className={`flex items-center gap-3 px-4 py-3 bg-cream-warm ${
                      idx !== priceAlerts.length - 1 ? 'border-b border-border-hairline' : ''
                    }`}
                  >
                    <span className="text-[20px] flex-shrink-0">{alert.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em] truncate">
                        {alert.name}
                      </div>
                      <div className="font-serif text-[11px] italic text-ink-muted mt-0.5">
                        avg ${alert.avgPrice.toFixed(2)} → now ${alert.latestPrice.toFixed(2)}
                      </div>
                    </div>
                    <span className="flex-shrink-0 rounded-full bg-critical-bg text-critical text-[11px] font-semibold px-2.5 py-1">
                      +{alert.pctChange}%
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Top items by spend ── */}
          {topItems.length > 0 && (
            <section className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-4">
                Top items by spend
              </div>
              <div className="flex flex-col gap-4">
                {topItems.map(item => {
                  const barWidth =
                    topItems[0].total > 0 ? (item.total / topItems[0].total) * 100 : 0
                  return (
                    <div key={item.id}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[16px] flex-shrink-0">{item.emoji}</span>
                        <span className="font-serif text-[14px] text-ink flex-1 truncate tracking-[-0.01em]">
                          {item.name}
                        </span>
                        <span className="font-serif text-[14px] font-medium text-ink flex-shrink-0">
                          ${item.total.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-[3px] bg-border-hairline rounded-full overflow-hidden">
                          <div
                            className="h-full bg-forest rounded-full transition-all"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                        <span className="text-[10px] italic font-serif text-ink-muted flex-shrink-0">
                          {item.count}× · avg ${item.avgPrice.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* ── Spending by store ── */}
          {topStores.length > 0 && (
            <section className="rounded-[18px] border border-border-sand bg-paper p-5">
              <div className="flex items-center gap-2 mb-4">
                <StoreIcon className="h-[14px] w-[14px] text-ink-muted" strokeWidth={1.7} />
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
                  Spending by store
                </div>
              </div>
              <div className="rounded-[14px] border border-border-sand overflow-hidden">
                {topStores.map((store, idx) => (
                  <div
                    key={store.name}
                    className={`flex items-center gap-3 px-4 py-3 bg-paper ${
                      idx !== topStores.length - 1 ? 'border-b border-border-hairline' : ''
                    }`}
                  >
                    <div className="h-9 w-9 rounded-full bg-forest text-forest-ink flex-shrink-0 flex items-center justify-center font-serif text-[14px] font-medium">
                      {store.name[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em] truncate">
                        {store.name}
                      </div>
                      <div className="font-serif text-[11px] italic text-ink-muted">
                        {store.count} {store.count === 1 ? 'purchase' : 'purchases'}
                      </div>
                    </div>
                    <span className="font-serif text-[14px] font-medium text-ink flex-shrink-0">
                      ${store.total.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

        </div>
      )}
    </main>
  )
}
